import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  InputGroup,
  Alert,
  Pagination,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  CalendarEvent,
  Clock,
  CheckCircle,
  Search,
  InfoCircle,
  Person,
  GraphUp,
  People,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';

// === Données des patients (identique aux autres pages) ===
const allPatients = [
  { id: 'CNAMGS-123456', name: 'Jean Dupont' },
  { id: 'CNAMGS-789012', name: 'Marie Nzogho' },
  { id: 'CNAMGS-345678', name: 'Pierre Mba' },
  { id: 'CNAMGS-111222', name: 'Sophie Nguema' },
  { id: 'CNAMGS-333444', name: 'Paul Obiang' },
  { id: 'CNAMGS-555666', name: 'Claire Moussavou' },
  { id: 'CNAMGS-777888', name: 'David Ngoma' },
  { id: 'CNAMGS-999000', name: 'Anne Mintogo' },
];

// Données fictives des rendez-vous (pour les 8 patients)
const initialAppointments = [
  { id: 'A001', date: '2025-04-05', time: '10:30', patientId: 'CNAMGS-123456', specialty: 'Médecine générale', reason: 'Consultation de suivi', status: 'Confirmé' },
  { id: 'A002', date: '2025-04-05', time: '14:00', patientId: 'CNAMGS-789012', specialty: 'Cardiologie', reason: 'Contrôle annuel', status: 'Confirmé' },
  { id: 'A003', date: '2025-04-06', time: '09:00', patientId: 'CNAMGS-345678', specialty: 'Médecine générale', reason: 'Consultation', status: 'En attente' },
  { id: 'A004', date: '2025-04-07', time: '11:00', patientId: 'CNAMGS-111222', specialty: 'Allergologie', reason: 'Suivi allergie', status: 'Confirmé' },
  { id: 'A005', date: '2025-04-07', time: '15:30', patientId: 'CNAMGS-333444', specialty: 'Endocrinologie', reason: 'Contrôle diabète', status: 'Confirmé' },
  { id: 'A006', date: '2025-04-08', time: '08:30', patientId: 'CNAMGS-555666', specialty: 'Médecine générale', reason: 'Fatigue', status: 'En attente' },
  { id: 'A007', date: '2025-04-08', time: '13:00', patientId: 'CNAMGS-777888', specialty: 'Cardiologie', reason: 'Suivi tension', status: 'Confirmé' },
  { id: 'A008', date: '2025-04-09', time: '10:00', patientId: 'CNAMGS-999000', specialty: 'Gynécologie', reason: 'Contrôle annuel', status: 'Confirmé' },
  { id: 'A009', date: '2025-04-10', time: '09:30', patientId: 'CNAMGS-123456', specialty: 'Médecine générale', reason: 'Vaccination', status: 'En attente' },
];

const Agenda = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  // Filtrer et trier
  const filteredAppointments = useMemo(() => {
    let filtered = initialAppointments.filter(a =>
      (allPatients.find(p => p.id === a.patientId)?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.specialty.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Tri
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'date') {
        aVal = new Date(a.date);
        bVal = new Date(b.date);
      } else if (sortField === 'patientName') {
        aVal = allPatients.find(p => p.id === a.patientId)?.name || '';
        bVal = allPatients.find(p => p.id === b.patientId)?.name || '';
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [searchTerm, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const paginatedAppointments = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAppointments.slice(start, start + itemsPerPage);
  }, [filteredAppointments, currentPage, itemsPerPage]);

  // Statistiques
  const today = new Date().toISOString().split('T')[0];
  const total = filteredAppointments.length;
  const todayCount = filteredAppointments.filter(a => a.date === today).length;
  const confirmed = filteredAppointments.filter(a => a.status === 'Confirmé').length;
  const pending = filteredAppointments.filter(a => a.status === 'En attente').length;

  const statsCards = [
    { title: 'Total rendez-vous', value: total, icon: CalendarEvent, color: '#4361ee', gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)', bg: 'white' },
    { title: "Aujourd'hui", value: todayCount, icon: Clock, color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', bg: 'white' },
    { title: 'Confirmés', value: confirmed, icon: CheckCircle, color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', bg: 'white' },
    { title: 'En attente', value: pending, icon: GraphUp, color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', bg: 'white' },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    if (status === 'Confirmé') return <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Confirmé</Badge>;
    if (status === 'En attente') return <Badge bg="warning" className="rounded-pill px-3 py-2"><Clock size={12} className="me-1" /> En attente</Badge>;
    return <Badge bg="secondary">{status}</Badge>;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUp size={12} className="ms-1" /> : <ArrowDown size={12} className="ms-1" />;
  };

  // Pagination controls
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
        {i}
      </Pagination.Item>
    );
  }

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <h4 className="main-title mb-0 d-flex align-items-center gap-2">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2">
              <CalendarEvent size={24} className="text-primary" />
            </div>
            Agenda des rendez-vous
          </h4>
          <p className="text-muted mb-0 mt-1">
            Consultez et gérez vos rendez-vous à venir
          </p>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-muted mb-1 small">{card.title}</h6>
                      <h3 className="mb-0 fw-bold">{card.value}</h3>
                    </div>
                    <div className="icon-container bg-opacity-10" style={{ background: `${card.color}20`, borderRadius: '12px', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <card.icon size={28} style={{ color: card.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barre de recherche et options */}
        <Card className="border-0 shadow-sm mb-4 bg-white" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <Row className="align-items-center g-3">
              <Col md={6} xs={6}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0">
                    <Search size={18} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher par patient, motif ou spécialité..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border-0 bg-light"
                  />
                </InputGroup>
              </Col>
              <Col md={3} xs={6}>
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border-0 bg-light"
                >
                  <option value={5}>5 par page</option>
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="text-muted small text-md-end">
                  {filteredAppointments.length} rendez-vous trouvé(s)
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des rendez-vous avec adaptation mobile */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
            <h5 className="fw-semibold mb-0">Liste des rendez-vous</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle table-striped">
                <thead className="bg-light">
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('date')}>
                      Date {renderSortIcon('date')}
                    </th>
                    <th>Heure</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('patientName')}>
                      Patient {renderSortIcon('patientName')}
                    </th>
                    <th className="d-none d-sm-table-cell">Spécialité</th>
                    <th className="d-none d-sm-table-cell">Motif</th>
                    <th className="d-none d-sm-table-cell">Statut</th>
                    <th>Action</th>
                    <th className="d-sm-none text-center">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAppointments.length > 0 ? (
                    paginatedAppointments.map(app => {
                      const patient = allPatients.find(p => p.id === app.patientId);
                      const isExpanded = expandedRows.has(app.id);
                      return (
                        <React.Fragment key={app.id}>
                          {/* Main row */}
                          <tr>
                            <td data-label="Date" className="text-nowrap">{formatDate(app.date)}</td>
                            <td data-label="Heure">{app.time}</td>
                            <td data-label="Patient" className="fw-semibold">{patient?.name || app.patientId}</td>
                            <td data-label="Spécialité" className="d-none d-sm-table-cell">{app.specialty}</td>
                            <td data-label="Motif" className="d-none d-sm-table-cell">{app.reason}</td>
                            <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(app.status)}</td>
                            <td data-label="Action">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px' }}
                                onClick={() => navigate(`/doctor/patient-record/${app.patientId}`)}
                                title="Voir dossier patient"
                              >
                                <Person size={14} />
                              </Button>
                            </td>
                            <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                              <Button
                                variant="link"
                                onClick={() => toggleRow(app.id)}
                                className="p-0 text-secondary"
                                style={{ textDecoration: 'none' }}
                              >
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </Button>
                            </td>
                          </tr>
                          {/* Expanded row - visible only on mobile */}
                          {isExpanded && (
                            <tr className="d-sm-none detail-row">
                              <td colSpan="8" className="p-3 bg-light">
                                <div className="row g-2">
                                  <div className="col-12">
                                    <strong>Spécialité :</strong> {app.specialty}
                                  </div>
                                  <div className="col-12">
                                    <strong>Motif :</strong> {app.reason}
                                  </div>
                                  <div className="col-12">
                                    <strong>Statut :</strong> {getStatusBadge(app.status)}
                                  </div>
                                  <div className="col-12">
                                    <strong>Patient :</strong> {patient?.name || app.patientId}
                                  </div>
                                  <div className="col-12">
                                    <strong>Date & Heure :</strong> {formatDate(app.date)} à {app.time}
                                  </div>
                                </div>
                               </td>
                             </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-5">
                        <CalendarEvent size={48} className="text-muted mb-3" />
                        <p className="text-muted">Aucun rendez-vous trouvé</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="text-muted small">
                  Affichage de {(currentPage-1)*itemsPerPage+1} à {Math.min(currentPage*itemsPerPage, filteredAppointments.length)} sur {filteredAppointments.length} rendez-vous
                </div>
                <Pagination className="mb-0">
                  <Pagination.Prev
                    onClick={() => setCurrentPage(p => Math.max(1, p-1))}
                    disabled={currentPage === 1}
                  />
                  {paginationItems}
                  <Pagination.Next
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Gestion des rendez-vous</strong>
                  <span className="small">
                    Cliquez sur l’icône patient pour accéder au dossier médical complet.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }
          .icon-container {
            background: rgba(0,0,0,0.05);
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          /* Responsive table adjustments */
          @media (max-width: 768px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .table td[data-label] {
              display: table-cell;
            }
            .detail-row td {
              padding: 1rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default Agenda;